import { CMS_Content_Module_VirtualExperienceData } from './types';
import { VirtualExperienceFilterMenu } from './virtual-experience-filter-menu';
import { VirtualExperienceTopicsMenu } from './virtual-experience-topics-menu';
import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { useFullscreen, useOnClickOutside, useResponsive } from '@cue/hooks';

import { Button } from '@cue/atoms';

import { ExternalContent } from '@cue/molecules';
import { Modal, useModal } from '@cue/organisms';
import {
  IConfigDto,
  INavigationMenuCustom,
  INavigationMenuItems,
  IVideoAction,
  IVideoEvent,
  IVideoScene,
  IVideoSceneInfoBox,
  TDisplayMode,
  VideoEventType,
  VideoPlatformContainer,
  VideoPlatformContextProvider,
  VideoPlatformRef,
} from '@cuevideostory/core';
import {
  VideoTour,
  VideoTourRef,
  VirtualTourConfig,
  VirtualTourConfigSource,
  VirtualTourStepSource,
} from '@cuevideotour/core';

import { VideoStoryWayPoints } from '@cuevideowaypoints/core';
import { ShowroomTopics } from '@project/cue-api/virtual-experience/fetch-video-story-topics';
// TODO TOUR
import { useEffect, useMemo, useRef, useState } from 'react';
import { isMobileSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Mdx } from '@cue/utility';
import { useShowroomTracking } from '@project/specific/hooks/use-showroom-tracking';

export const SHOWROOM_TRACKING_PRE = 'experience';

export interface ShowroomTrackingBase {
  sessionId?: string;
}
export type ShowroomTrackingObject = ShowroomTrackingBase & Pick<IVideoScene, 'sceneKey' | 'name'>;

const parseTourConfig = (
  { tours, toursteps }: VirtualTourConfigSource,
  lang: string,
): VirtualTourConfig => {
  return {
    toursteps,
    tours: tours.map((tour) => {
      return {
        id: tour.id,
        title:
          tour?.translations.find(({ languages_code }) => languages_code?.code?.includes(lang))
            ?.title || '',
        description:
          tour?.translations.find(({ languages_code }) => languages_code?.code?.includes(lang))
            ?.description || '',
        steps: parseTourSteps(tour.steps, lang),
      };
    }),
  };
};
const parseTourSteps = (scenes: VirtualTourStepSource[], lang: string) => {
  return scenes.map((scene) => {
    const trans = scene?.translations.find(({ languages_code }) =>
      languages_code?.code?.includes(lang),
    );
    return {
      id: scene.id,
      sort: scene?.sort,
      scene: scene?.scene,
      title: trans?.title,
      tour_title: trans?.tour_title,
      description: trans?.description,
      audio_description: trans?.audio_description,
    };
  });
};
export const VirtualExperienceCore = ({
  ratio: _ratio = '16/9',
  displayMode = 'contain',
  videoConfigJSON,
  tourConfigJSON,
  videoTopicsJSON,
  videoWaypointsJSON,
  show_overlay = false,
  top_line,
  sub_line,
  text,
  path = '',
}: VirtualExperienceProps) => {
  const loadedConfig = useMemo(() => {
    return videoConfigJSON as unknown as IConfigDto;
  }, [videoConfigJSON]);

  const startscene = loadedConfig.startscene;

  const ratio = useMemo(() => {
    if (/^\d+(\/\d+)?$/.test(_ratio)) {
      return _ratio;
    } else {
      console.warn('Invalid Ratio, fallback to 16/9');
      return '16/9';
    }
  }, [_ratio]);
  const tracking = useShowroomTracking();
  if (process.env.TYPE === 'stage') tracking.debuggingEnabled();
  const fsRef = useRef<HTMLDivElement>(null);
  const tourRef = useRef<VideoTourRef>(null);
  const [modalRef] = useModal();
  const {
    i18n: { language },
  } = useTranslation();

  const [infoBoxOpen, setInfoBoxOpen] = useState(false);

  const { isMobile, isMedium } = useResponsive(fsRef);
  const isLarge = !(isMobile || isMedium);
  const [, setIsFullscreen] = useState(false);
  const [speakerActive] = useState(false);
  const [startSceneKey] = useState<string>(startscene);
  const [currentScene, setCurrentScene] = useState<IVideoScene>();
  const [currentTour, setCurrentTour] = useState<string | null>();
  const [tourTitle, setTourTitle] = useState<string | undefined>();
  const [hasWaypoints, setHasWaypoints] = useState(false);
  const [showOverlayState, setShowOverlayState] = useState(show_overlay);

  const [infoBox, setInfoBox] = useState<IVideoSceneInfoBox | null | false>(null);
  const { t, i18n } = useTranslation();
  const loadedTourConfig = parseTourConfig(
    tourConfigJSON as unknown as VirtualTourConfigSource,
    i18n.language,
  );

  // useEffect(() => {
  //   i18n.changeLanguage('en');
  // }, [i18n]);

  // const { hash } = useHash();
  const { exit: exitFullscreen, isFullscreen } = useFullscreen();
  const [fairtouchUrl, setFairtouchUrl] = useState('');

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [filterIsOpen, setFilterIsOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, (e) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!e?.target?.classList?.contains('cue-video-story-navigation-nav-active'))
      setMenuIsOpen(false);
  });

  const filterRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(filterRef, (e) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (!e?.target?.classList.contains('cue-video-story-navigation-nav-active'))
      setFilterIsOpen(false);
  });

  const onVideoEvent = (event: IVideoEvent) => {
    if (event.event === VideoEventType.VideoEnd) {
      setCurrentScene(event.scene);
      console.log('event>>> ENTER', event.scene.sceneKey);
    }
    if (event.event === VideoEventType.VideoStart) {
      //setInfoBox(null);

      if (event.video && !event.video.loop) {
        console.log('event>>> EXIT', event, event.scene.sceneKey);
      }
    }
  };

  const onAction = ({ action, params }: IVideoAction) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const url = params?.href[language];
    // const lcc = language === 'de' ? 'de-DE' : 'en-US';

    switch (action) {
      case 'tourentry':
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        tourRef?.current?.showTourEntry({ sceneId: params?.linkSceneId });
        break;
      case 'fairtouch':
        setFairtouchUrl(url);

        break;
      case 'modal':
        setFairtouchUrl(url);

        break;
      case 'https':
        window.open(url);
        exitFullscreen();

        break;
    }
  };

  useEffect(() => {
    if (fairtouchUrl) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [fairtouchUrl, modalRef]);

  const videoPlatformRef = useRef<VideoPlatformRef>();

  const location = useLocation();
  const experienceMatch = new URLSearchParams(location.search).get('experience');

  const stateHandler = {
    setState: (url: string) => {
      return navigate(`${path.replace(':id', '')}?experience=${url}#showroom`); //${location.hash ? `${location.hash}` : ''}
    },
    state: experienceMatch,
    initState: '',
  };

  /*   const isHome = useMemo(() => {
    const regexExp =
      /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
    const hashIsOK = regexExp.test(hash || '');
    if (!hashIsOK) return true;

    return hash === startSceneKey;
  }, [hash, startSceneKey]); */

  const menuItem: INavigationMenuCustom = {
    icon: 'navigation',
    active: menuIsOpen,
    onToggle: (active) => {
      setMenuIsOpen(active);
      setFilterIsOpen(false);
    },
  };

  const filterItem: INavigationMenuCustom = {
    icon: 'showroomFilter',
    active: filterIsOpen,
    disabled: currentScene?.sceneKey !== startscene,
    onToggle: (active) => {
      setFilterIsOpen(active);
      setMenuIsOpen(false);
    },
  };

  const addFS = isMobileSafari ? [] : [INavigationMenuItems.FULLSCREEN];

  const refVideo = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    if (!refVideo.current) {
      return;
    }

    //open bug since 2017 that you cannot set muted in video element https://github.com/facebook/react/issues/10389
    refVideo.current.defaultMuted = true;
    refVideo.current.muted = true;
    refVideo.current
      .play()
      .then(() => {
        console.log('No low power mode');
      })
      .catch((error: Error) => {
        console.log('Low Power mode!', error);
        if (String(error).startsWith('NotAllowedError')) {
          setVideoError(true);
        }
      });
  }, []);

  return (
    <VideoPlatformContextProvider>
      <a id="virtual-experience" className="cue-content-module-anchor"></a>
      <div
        ref={fsRef}
        style={{ position: 'relative' }}
        className={`virtual-experience ${currentScene?.sceneKey === startscene ? 'is-home' : ''}`}>
        {!experienceMatch && showOverlayState ? (
          <div
            className="virtual-experience-overlay"
            onClick={(e) => {
              e.stopPropagation();
              setShowOverlayState(false);
            }}>
            <div className="bg">
              <video
                autoPlay
                loop
                playsInline
                muted
                // eslint-disable-next-line react/no-unknown-property
                webkit-playsinline=""
                ref={refVideo}>
                <source src="/assets/video/video-story-overlay-bg.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            {videoError ? (
              <div
                className="virtual-experience-overlay__video-error"
                onClick={(e) => {
                  e.stopPropagation();
                }}>
                <div className="virtual-experience-overlay__video-error-content">
                  <h3>{t('virtual-experience.video-error.title')}</h3>
                  <p>{t('virtual-experience.video-error.text')}</p>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.reload();
                    }}>
                    {t('virtual-experience.video-error.btn')}
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="virtual-experience-overlay__content">
              <h2 className="virtual-experience-overlay__thin">{top_line}</h2>
              <h2 className="virtual-experience-overlay__bold">{sub_line}</h2>
              <Mdx className="virtual-experience-overlay__text">{String(text)}</Mdx>
              <div className="virtual-experience-overlay__btns">
                <Button
                  className="virtual-experience-overlay__enter-btn"
                  styling="default"
                  onClick={() => {
                    setShowOverlayState(false);
                  }}>
                  {t('virtual-experience.overlay.cta')}
                </Button>
              </div>
            </div>
            <div className="virtual-experience-overlay__logo"></div>
          </div>
        ) : null}
        <VirtualExperienceTopicsMenu
          stateHandler={stateHandler}
          visible={menuIsOpen}
          onHideMenu={() => setMenuIsOpen(false)}
          ref={menuRef}
          videoTopicsJSON={videoTopicsJSON}
        />
        <VirtualExperienceFilterMenu
          visible={filterIsOpen}
          wrapperRef={fsRef}
          onHideMenu={() => setMenuIsOpen(false)}
          ref={filterRef}
          videoTopicsJSON={videoTopicsJSON}
        />
        <Modal
          ref={modalRef}
          className="fairtouch"
          style={{ zIndex: 9999 }}
          maxWidth={'100vw'}
          renderInPlace={isFullscreen}
          onVisibilityChange={(show) => {
            if (!show) setFairtouchUrl('');
          }}
          customCloseRenderer={() => (
            <Button
              icon="close"
              data-test-id="cue-test-modal-close"
              styling="modalClosePlain"
              onClick={() => {
                setFairtouchUrl('');
                tracking.closeContent();
              }}
            />
          )}>
          <div className="cue-virtual-experience-fairtouch-modal">
            <ExternalContent uri={fairtouchUrl} />
          </div>
        </Modal>
        <VideoPlatformContainer
          stateHandler={stateHandler}
          trackingHandler={tracking}
          version="V2"
          ratio={ratio}
          config={loadedConfig}
          startSceneKey={startSceneKey.toString()}
          fsWrapper={fsRef}
          displayMode={displayMode}
          overrideSceneName={tourTitle}
          onAction={onAction}
          onVideoEvent={onVideoEvent}
          onFullscreenchange={setIsFullscreen}
          dimSound={speakerActive}
          buttonSafeZone={[77, 24, currentTour || hasWaypoints ? 91 : 24, 24]}
          hideInvalidLinks={true}
          infoBox={infoBox}
          primaryMenuInclude={
            isLarge
              ? [menuItem, filterItem, INavigationMenuItems.SOUND]
              : [
                  ...[
                    menuItem,
                    INavigationMenuItems.HOME,
                    INavigationMenuItems.BACK,
                    filterItem,
                    INavigationMenuItems.SOUND,
                  ],
                  ...addFS,
                ]
          }
          secondaryMenuInclude={
            isLarge
              ? [
                  INavigationMenuItems.HOME,
                  INavigationMenuItems.BACK,
                  INavigationMenuItems.DIVIDER,
                  INavigationMenuItems.FULLSCREEN,
                ]
              : null
          }
          ref={videoPlatformRef}
          onInfoBoxOpen={(open) => setInfoBoxOpen(open)}
        />
        <VideoTour
          stateHandler={stateHandler}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          trackingHandler={tracking}
          config={loadedTourConfig}
          currentScene={currentScene}
          displayMode={displayMode}
          ref={tourRef}
          stepOptions={{ isMobile: !isLarge, showTitle: isLarge }}
          onChangeTour={(tourstep) => {
            setTourTitle(tourstep?.title || tourstep?.tour_title || '');
            if (currentScene && loadedTourConfig?.toursteps?.[currentScene?.sceneKey]) {
              // ENTER TOUR
              if (tourstep) setCurrentTour(tourstep.tour_id);
            } else {
              // EXIT TOUR
              setCurrentTour(null);
              //setInfoBox(null);
            }
          }}
          onChangeDescription={(data) => {
            setInfoBox(data);
          }}
          onExitTour={({ is_last }) => {
            console.log('EXIT');
            const exit_scene = currentScene?.exit_scene || startSceneKey;
            if (is_last) {
              stateHandler.setState(exit_scene);
            }

            // CHECK IF ITS WAYPOINT
            if (
              !videoWaypointsJSON?.find((d) => {
                return d.scenes.find((s) => s.id === currentScene?.sceneKey);
              })
            )
              setInfoBox(null);

            setCurrentTour(null);
            setTimeout(() => setTourTitle(''), 150); // TODO Prevent flicker
          }}
        />
        {/*         <pre>{JSON.stringify(videoWaypointsJSON, null, 2)}</pre>
         */}{' '}
        <VideoStoryWayPoints
          data={videoWaypointsJSON as unknown as VideoStoryWayPoints[]}
          scene={stateHandler?.state || ''}
          onChange={(scene) => stateHandler.setState(scene)}
          infoBoxOpen={infoBoxOpen}
          stepOptions={{
            isMobile: !isLarge,
          }}
          onUpdate={({ waypoint, infoBox }) => {
            setHasWaypoints(!!waypoint);
            setInfoBox(infoBox);
          }}
        />
      </div>
    </VideoPlatformContextProvider>
  );
};
export interface VirtualExperienceProps extends VirtualExperienceWrapperProps {
  videoConfigJSON: IConfigDto;
  tourConfigJSON: VirtualTourConfigSource;
  videoTopicsJSON: ShowroomTopics[];
  videoWaypointsJSON: VideoStoryWayPoints[];
  default?: boolean;
  path?: string;
  uri?: string;
}

export interface VirtualExperienceWrapperProps extends CMS_Content_Module_VirtualExperienceData {
  showTitle?: boolean;
  fallback?: React.ReactNode;
  disableAudio?: boolean;
  disableFullscreen?: boolean;
  disableTourDescriptions?: boolean;
  ratio?: string;
  displayMode?: TDisplayMode;
  onChangeScene?(target: string): void;
  show_overlay?: boolean;
  top_line?: string;
  sub_line?: string;
  text?: string;
}
